import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./umpireList.css";

// Dummy data for initial display
const DUMMY_GAMES = [
  {
    id: 1,
    matchNumber: 101,
    tournamentName: "Summer Championship",
    eventName: "Singles U19",
    roundName: "Quarter Final",
    matchDateTime: "2024-12-10T09:00:00",
    courtNumber: "Court 1",
    team1Name: "John Smith",
    team2Name: "Mike Johnson",
    status: "Scheduled"
  }
];

function UmpireList() {
  const navigate = useNavigate();
  const [games, setGames] = useState(DUMMY_GAMES);
  const [filteredGames, setFilteredGames] = useState(DUMMY_GAMES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewGame, setViewGame] = useState(null);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchGames();
  }, []);

  useEffect(() => {
    // Filter out completed matches first
    let filtered = games.filter(game => 
      game.status?.toLowerCase() !== 'completed'
    );

    // Then apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(game => {
        const matchNumberStr = game.matchNumber?.toString() || "";
        const matchNumberMatch = matchNumberStr.includes(searchTerm.trim());
        const tournamentMatch = (game.tournamentName || "").toLowerCase().includes(searchLower);
        const eventMatch = (game.eventName || "").toLowerCase().includes(searchLower);
        const roundMatch = (game.roundName || "").toLowerCase().includes(searchLower);
        const courtMatch = (game.courtNumber || "").toLowerCase().includes(searchLower);
        const team1Match = (game.team1Name || "").toLowerCase().includes(searchLower);
        const team2Match = (game.team2Name || "").toLowerCase().includes(searchLower);
        const statusMatch = (game.status || "").toLowerCase().includes(searchLower);

        return matchNumberMatch || tournamentMatch || eventMatch || roundMatch ||
          courtMatch || team1Match || team2Match || statusMatch;
      });
    }

    setFilteredGames(filtered);
  }, [searchTerm, games]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://api.asterickx.com/api/Matches",
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          alert("Authentication failed. Please login again.");
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("=== RAW API RESPONSE ===");
      console.log("Full API data:", data);

      // Handle both array and $values wrapper format
      let matchesArray = [];
      if (Array.isArray(data)) {
        matchesArray = data;
      } else if (data.$values && Array.isArray(data.$values)) {
        matchesArray = data.$values;
      }

      console.log("Matches array length:", matchesArray.length);

      // Map the data to include display names
      const mappedGames = matchesArray.map(match => {
        const isTeam = match.isTeam || match.isTeamMatch || match.IsTeamMatch || false;
        
        // Extract player names - API uses lowercase p1A, p1B, p2A, p2B
        const p1A = match.p1A || match.player1AName || match.Player1AName || "";
        const p1B = match.p1B || match.player1BName || match.Player1BName || "";
        const p2A = match.p2A || match.player2AName || match.Player2AName || "";
        const p2B = match.p2B || match.player2BName || match.Player2BName || "";
        
        let team1Display = "";
        let team2Display = "";
        
        // Always check if we have multiple players, regardless of isTeam flag
        const team1Players = [p1A, p1B].filter(name => name && name.trim() !== "");
        const team2Players = [p2A, p2B].filter(name => name && name.trim() !== "");
        
        // If we have 2 players on either side, it's a doubles match - show both
        if (team1Players.length > 1 || team2Players.length > 1 || isTeam) {
          team1Display = team1Players.length > 0 ? team1Players.join(" & ") : "TBD";
          team2Display = team2Players.length > 0 ? team2Players.join(" & ") : "TBD";
        } else {
          // Singles match - show only first player
          team1Display = p1A || "TBD";
          team2Display = p2A || "TBD";
        }

        console.log("Match processing:", {
          id: match.id || match.Id,
          isTeam,
          p1A, p1B, p2A, p2B,
          team1Players,
          team2Players,
          team1Display,
          team2Display
        });

        return {
          id: match.id || match.Id,
          matchNumber: match.matchNumber || match.MatchNumber,
          tournamentId: match.tournamentId || match.TournamentId,
          tournamentName: match.tournamentName || match.TournamentName,
          tournamentEventId: match.tournamentEventId || match.TournamentEventId,
          eventName: match.eventName || match.EventName || match.eventCategoryDisplayName || match.EventCategoryDisplayName,
          roundId: match.roundId || match.RoundId,
          roundName: match.roundName || match.RoundName,
          matchDateTime: match.matchDateTime || match.MatchDateTime,
          courtNumber: match.courtNumber || match.CourtNumber,
          team1Id: match.team1Id || match.Team1Id,
          team2Id: match.team2Id || match.Team2Id,
          team1Name: team1Display,
          team2Name: team2Display,
          player1AId: match.player1AId || match.Player1AId,
          player1BId: match.player1BId || match.Player1BId,
          player2AId: match.player2AId || match.Player2AId,
          player2BId: match.player2BId || match.Player2BId,
          isTeamMatch: isTeam,
          status: (match.status || match.Status || "N/A").trim()
        };
      });

      console.log("=== MAPPED GAMES ===");
      console.log("Mapped games:", mappedGames);
      console.log("=== STATUS DEBUG ===");
      console.log("Total matches from API:", mappedGames.length);
      console.log("Completed matches (will be filtered out):", mappedGames.filter(g => 
        g.status?.toLowerCase() === 'completed'
      ).length);
      console.log("Unique statuses in data:", [...new Set(mappedGames.map(g => g.status))]);

      const mergedGames = mappedGames.length > 0 ? mappedGames : DUMMY_GAMES;
      setGames(mergedGames);
      
      // Filter out completed matches immediately
      const nonCompletedGames = mergedGames.filter(game => 
        game.status?.toLowerCase() !== 'completed'
      );
      setFilteredGames(nonCompletedGames);
      
      console.log("Non-completed matches displayed:", nonCompletedGames.length);
    } catch (err) {
      console.error("Error fetching games:", err);
      setError("Failed to load matches. Showing dummy data.");
      setGames(DUMMY_GAMES);
      setFilteredGames(DUMMY_GAMES);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleView = (game) => {
    setViewGame(game);
    setShowViewModal(true);
  };

  const handleSchedule = (game) => {
    console.log("Navigating to match score page with game:", game);
    navigate(`/matchscore/${game.id}`, { state: { game } });
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewGame(null);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateTimeString;
    }
  };

  const extractDate = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "N/A";
    }
  };

  const extractTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="um-container">
      {/* Header Section */}
      <div className="um-header-section">
        <h2 className="um-page-title">Umpire Assignment</h2>
        
        <div className="um-right-controls">
          <div className="um-search-container">
            <input
              type="text"
              className="um-search-input"
              placeholder="Search matches..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <svg
              className="um-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>
        </div>
      </div>

      {error && <p className="um-error-message">{error}</p>}

      {searchTerm && (
        <div className="um-search-results-info">
          Showing {filteredGames.length} of {games.filter(g => g.status?.toLowerCase() !== 'completed').length} matches (excluding completed)
        </div>
      )}

      {/* Responsive Table with Horizontal Scroll */}
      <div className="um-table-container um-responsive-table-wrapper">
        <table className="um-table">
          <thead>
            <tr>
              <th>Match #</th>
              <th>Tournament</th>
              <th>Event</th>
              <th>Round</th>
              <th>Date</th>
              <th>Time</th>
              <th>Player 1</th>
              <th>Player 2</th>
              <th>Court</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="um-text-center">
                  <div className="um-loader"></div>
                  Loading...
                </td>
              </tr>
            ) : filteredGames.length > 0 ? (
              filteredGames.map((game, index) => {
                return (
                  <tr key={game.id || index}>
                    <td className="um-match-number">{game.matchNumber || "N/A"}</td>
                    <td>{game.tournamentName || "N/A"}</td>
                    <td>{game.eventName || "N/A"}</td>
                    <td>
                      <span className="um-round-badge">
                        {game.roundName || "N/A"}
                      </span>
                    </td>
                    <td>
                      <span className="um-day-badge">
                        {extractDate(game.matchDateTime)}
                      </span>
                    </td>
                    <td>{extractTime(game.matchDateTime)}</td>
                    <td className="um-player-cell">
                      <div>
                        {game.team1Name || "N/A"}
                        {game.isTeamMatch && <span className="um-team-badge">Team</span>}
                      </div>
                    </td>
                    <td className="um-player-cell">
                      <div>
                        {game.team2Name || "N/A"}
                        {game.isTeamMatch && <span className="um-team-badge">Team</span>}
                      </div>
                    </td>
                    <td>
                      <span className="um-court-badge">
                        {game.courtNumber || "N/A"}
                      </span>
                    </td>
                    <td>
                      <span className={`um-status-badge um-status-${game.status?.toLowerCase()}`}>
                        {game.status || "N/A"}
                      </span>
                    </td>
                    <td>
                      <div className="um-action-buttons">
                        <button
                          className="um-view-btn"
                          onClick={() => handleView(game)}
                          title="View Details"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        <button
                          className="um-schedule-btn"
                          onClick={() => handleSchedule(game)}
                          title="Assign Umpire"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="um-text-center">
                  {searchTerm ? "No matches found matching your search" : "No matches available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      {showViewModal && viewGame && (
        <div className="um-modal-overlay" onClick={(e) => {
          if (e.target.className === "um-modal-overlay") {
            closeViewModal();
          }
        }}>
          <div className="um-modal-container um-view-modal">
            <div className="um-modal-header">
              <h3>Match Details</h3>
              <button className="um-close-btn" onClick={closeViewModal}>×</button>
            </div>

            <div className="um-modal-body">
              <div className="um-view-content">
                <div className="um-view-row">
                  <div className="um-view-label">Match Number:</div>
                  <div className="um-view-value um-highlight">{viewGame.matchNumber || "N/A"}</div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Tournament:</div>
                  <div className="um-view-value">{viewGame.tournamentName || "N/A"}</div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Event:</div>
                  <div className="um-view-value">{viewGame.eventName || "N/A"}</div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Round:</div>
                  <div className="um-view-value">
                    <span className="um-round-badge">{viewGame.roundName || "N/A"}</span>
                  </div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Date & Time:</div>
                  <div className="um-view-value">{formatDateTime(viewGame.matchDateTime)}</div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Match Type:</div>
                  <div className="um-view-value">
                    <span className={`um-match-type-badge ${viewGame.isTeamMatch ? 'um-team' : 'um-singles'}`}>
                      {viewGame.isTeamMatch ? "Team (Doubles)" : "Singles"}
                    </span>
                  </div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Player 1:</div>
                  <div className="um-view-value um-player-name">
                    {viewGame.team1Name || "N/A"}
                  </div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Player 2:</div>
                  <div className="um-view-value um-player-name">
                    {viewGame.team2Name || "N/A"}
                  </div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Court:</div>
                  <div className="um-view-value">
                    <span className="um-court-badge">{viewGame.courtNumber || "N/A"}</span>
                  </div>
                </div>

                <div className="um-view-row">
                  <div className="um-view-label">Status:</div>
                  <div className="um-view-value">
                    <span className={`um-status-badge um-status-${viewGame.status?.toLowerCase()}`}>
                      {viewGame.status || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="um-view-actions">
                <button
                  className="um-schedule-from-view-btn"
                  onClick={() => {
                    closeViewModal();
                    handleSchedule(viewGame);
                  }}
                >
                  📅 Assign Umpire
                </button>
                <button className="um-close-view-btn" onClick={closeViewModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UmpireList;